// /resources/js/app/modules/v2/V2TemplateBuilder.js
import { formatPct, formatNumber } from '../../../utils/formatters.js';
import * as Icons from '../../../ui/icons.js';
import { formatDistanceToNow } from 'date-fns';

/**
 * @module V2TemplateBuilder
 * This module is a dedicated "HTML factory" for the V2 feed. Its sole responsibility
 * is to take structured data objects and return fully formed HTML strings. It is

 * completely stateless and has no knowledge of the DOM.
 */
class V2TemplateBuilder {    

    buildSignalCard(consolidatedSignal, isMySignal = false) {
        if (!consolidatedSignal || !consolidatedSignal.variants || consolidatedSignal.variants.length === 0) return '';

        const asset = consolidatedSignal.asset_info;
        const variants = consolidatedSignal.variants;
        const defaultVariant = variants[0];

        const isLocked = defaultVariant.lock_status !== 'unlocked';
        const styleClasses = this._getCardStyleClasses(defaultVariant, isMySignal, isLocked);

        const alpineData = `x-data="{ activeVariantIndex: 0, totalVariants: ${variants.length} }"`;

        const headerHtml = this._buildCardHeader(defaultVariant, asset, isLocked);
        const actionsHtml = this._buildCardActionIcons(defaultVariant, asset);
        const sparklineHtml = this._renderSparkline(defaultVariant, consolidatedSignal.sparkline_1h);

        // The dynamic parts are now built by methods that handle the looping internally
        const marketDataHtml = this._buildCardMarketDataGrid(variants);
        const sourceDataHtml = this._buildCardSourceGrid(variants);
        const footerHtml = this._buildCardFooter(variants);

        return `
            <div data-asset-id="${asset.id}" ${alpineData}
                class="bg-gradient-to-br ${styleClasses.glow} rounded-2xl p-2 group transition-all duration-300">
                
                <div class="bg-surface/50 backdrop-blur-lg rounded-xl border border-border/80 p-4 flex flex-col h-full space-y-3">
                    ${headerHtml}
                    ${actionsHtml}
                    <div class="h-16 rounded flex items-center justify-center">${sparklineHtml}</div>
                    
                    ${marketDataHtml}
                    ${sourceDataHtml}
                    ${footerHtml}
                </div>
            </div>
        `;
    }

    _renderIcon(iconConstant, isLocked = false) {
        if (!iconConstant) return '';

        // Handle SVG strings by replacing their size class
        if (iconConstant.startsWith('<svg')) {
            return iconConstant.replace('w-4 h-4', 'w-5 h-5');
        }

        // Handle image paths
        if (iconConstant.endsWith('.png') || iconConstant.endsWith('.jpg') || iconConstant.endsWith('.jpeg')) {
            const grayscaleClass = isLocked ? '' : '';
            return `<img src="${iconConstant}" class="w-5 h-5 ${grayscaleClass}" alt="icon">`;
        }

        return '';
    }    
    
    _buildCardActionIcons(signal, asset) {
        const isLocked = signal.lock_status !== 'unlocked';
        let iconsHtml = '';

        if (isLocked) {
            // Render clickable buttons for locked signals that trigger the unlock flow
            const unlockPayload = JSON.stringify({
                call_id: signal.call_id, 
                cost: signal.unlock_cost
            }).replace(/"/g, "'");

            const disabledIcons = [
                { icon: Icons.DEXTOOLS_ICON, title: 'DEXTools' },
                { icon: Icons.DEXSCREENER_ICON, title: 'DexScreener' },
                (asset.chain_id === 'solana' ? { icon: Icons.RAYDIUM_ICON, title: 'Raydium' } : { icon: Icons.UNISWAP_ICON, title: 'Uniswap' }),
                { icon: Icons.BANANA_GUN_ICON, title: 'Banana Gun Bot' }
            ];

            iconsHtml = disabledIcons.map(item => 
                `<button @click="$dispatch('open-unlock-flow', ${unlockPayload})" title="${item.title}" class="text-text-dark hover:text-text-muted transition-colors">${this._renderIcon(item.icon, true)}</button>`
            ).join('');

        } else {
            // Render fully functional, clickable links for unlocked signals
            if (!asset || !asset.chain_id || !asset.token_address) return '<div class="h-5"></div>';

            const chain = asset.chain_id;
            const tokenAddress = asset.token_address;
            const poolAddress = asset.pool_address;
            const dextoolsChain = chain === 'ethereum' ? 'ether' : chain;
            
            const links = [];

            if (poolAddress) {
                links.push(`<a href="https://www.dextools.io/app/en/${dextoolsChain}/pair-explorer/${poolAddress}" target="_blank" rel="noopener noreferrer" title="DEXTools" class="text-text-muted hover:text-primary">${this._renderIcon(Icons.DEXTOOLS_ICON)}</a>`);
                links.push(`<a href="https://dexscreener.com/${chain}/${poolAddress}" target="_blank" rel="noopener noreferrer" title="DexScreener" class="text-text-muted hover:text-primary">${this._renderIcon(Icons.DEXSCREENER_ICON)}</a>`);
            }
            if (chain === 'solana') {
                links.push(`<a href="https://raydium.io/swap/?inputMint=sol&outputMint=${tokenAddress}&referrer=9bqsiPbu59gANfrhoGpmGGHgNtxoubrfsw5QJwS9Uvzh" target="_blank" rel="noopener noreferrer" title="Raydium" class="text-text-muted hover:text-primary">${this._renderIcon(Icons.RAYDIUM_ICON)}</a>`);
                links.push(`<a href="https://t.me/BananaGun_bot?start=ref_upai_${tokenAddress}" target="_blank" rel="noopener noreferrer" title="BananaGun Bot" class="text-text-muted hover:text-primary">${this._renderIcon(Icons.BANANA_GUN_ICON)}</a>`);
            }
            if (chain === 'ethereum' || chain === 'base' || chain === 'ETHEREUM' || chain === 'BASE') {
                links.push(`<a href="https://app.uniswap.org/explore/tokens/${chain}/${tokenAddress}" target="_blank" rel="noopener noreferrer" title="Uniswap" class="text-text-muted hover:text-primary">${this._renderIcon(Icons.UNISWAP_ICON)}</a>`);
                links.push(`<a href="https://t.me/BananaGunSniper_bot?start=ref_upai_${tokenAddress}" target="_blank" rel="noopener noreferrer" title="BananaGun Bot" class="text-text-muted hover:text-primary">${this._renderIcon(Icons.BANANA_GUN_ICON)}</a>`);
            }
               
            const metadata = asset.metadata_json || {};
            const socialLinks = [];

            if (metadata.website) {
                const websiteIcon = `<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>`;
                socialLinks.push(`<a href="${metadata.website}" target="_blank" rel="noopener noreferrer" title="Website" class="text-white hover:text-primary">${this._renderIcon(websiteIcon)}</a>`);
            }
            if (metadata.twitter) {
                socialLinks.push(`<a href="${metadata.twitter}" target="_blank" rel="noopener noreferrer" title="Twitter/X" class="text-text-muted hover:text-primary">${this._renderIcon('/images/x_logo.png')}</a>`);
            }
            if (metadata.telegram) {
                socialLinks.push(`<a href="${metadata.telegram}" target="_blank" rel="noopener noreferrer" title="Telegram" class="text-text-muted hover:text-primary">${this._renderIcon('/images/telegram.png')}</a>`);
            }

            if (links.length > 0 && socialLinks.length > 0) {
                links.push('<div class="w-px h-4 bg-border mx-1"></div>');
            }
            links.push(...socialLinks);
            // --- END: NEW SOCIAL & WEBSITE ICONS ---

            iconsHtml = links.join('');
        }

        return `<div class="flex items-center space-x-3 -mt-1 h-5">${iconsHtml}</div>`;
    }
    _getCardStyleClasses(signal, isMySignal, isLocked) {
        const tier = signal.source_tier || 'bronze';
        const baseTierInfo = this._getTierClasses(tier);

        let finalGlow;

        // "My Signals" will have the special Gold styling.
        if (isMySignal) {
            finalGlow = 'from-tier-gold/60 via-background/20 to-background';
        } 
        // Locked signals will have the muted Silver styling.
        else if (isLocked) {
            finalGlow = 'from-tier-silver/40 via-slate-800/50 to-background';
        } 
        // Unlocked signals use their source's tier gradient.
        else {
            finalGlow = baseTierInfo.glow;
        }

        // The method now only needs to return the gradient and the icon.
        // The border and inner panel styles will be hardcoded in the main builder.
        return {
            glow: finalGlow,
            icon: baseTierInfo.icon
        };
    }_buildCardHeader(signal, asset, isLocked) {
        const imageUrl = asset.metadata_json?.imageUrl;
        const imageProxyUrl = window.SGNL?.config?.image_proxy_url;

        const placeholderIcon = isLocked
            ? `<div class="w-8 h-8 rounded-full bg-secondary flex-shrink-0 flex items-center justify-center font-bold">?</div>`
            : `<div class="w-8 h-8 rounded-full bg-secondary flex-shrink-0 flex items-center justify-center font-bold">${(asset.symbol || '?').charAt(0)}</div>`;
        
        let imageHtml;
        
        if (imageUrl) {
            imageHtml = `<img src="${imageUrl}" alt="Token" class="w-8 h-8 rounded-full">`;
        } else {
            imageHtml = placeholderIcon;
        }

        // --- START: MODIFIED SECTION ---
        const copyIcon = `<svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>`;
        const checkIcon = `<svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>`;

        const copyButtonHtml = `
            <button x-data="{ copied: false }" 
                    @click.stop="navigator.clipboard.writeText('${asset.token_address}'); copied = true; setTimeout(() => copied = false, 2000);"
                    title="Copy Contract Address"
                    class="ml-1.5 p-1 rounded-full text-text-dark hover:bg-secondary hover:text-primary transition-colors focus:outline-none">
                <span x-show="!copied" class="block">${copyIcon}</span>
                <span x-show="copied" x-cloak class="block text-success">${checkIcon}</span>
            </button>
        `;

        const titleHtml = isLocked
            ? `<p class="text-sm font-bold text-text-muted">[LOCKED SGNL]</p>`
            : `
                <div class="flex items-center min-w-0">
                    <p class="text-sm font-bold text-text-base truncate">$${asset.symbol}</p>
                    ${copyButtonHtml}
                </div>
            `;
        // --- END: MODIFIED SECTION ---

        const chain = asset.chain_id?.toLowerCase() || 'unknown';
        const chainLogoMap = {
            'ethereum': '/images/ethereum.png',
            'solana': '/images/solana.png',
            'base': '/images/base.png'
        };

        const chainIconHtml = chainLogoMap[chain]
            ? `<img src="${chainLogoMap[chain]}" alt="${chain} logo" class="w-5 h-5">`
            : `<div class="w-5 h-5 rounded-full bg-secondary flex items-center justify-center text-xs font-bold text-text-dark">?</div>`;
        
        const timeSince = new Date(signal.call_timestamp);
        const timeAgo = formatDistanceToNow(timeSince, { addSuffix: true });

        return `
            <div class="flex items-center justify-between">
                <div class="flex items-center space-x-2 min-w-0">
                    ${imageHtml}
                    <div class="min-w-0">
                        ${titleHtml}
                        <p class="text-xs text-text-muted font-mono" title="${timeSince.toUTCString()}">${timeAgo}</p>
                    </div>
                </div>
                ${chainIconHtml}
            </div>`;
    }

    _buildCardMarketDataGrid(variants) {
        const panels = variants.map((signal, index) => {
            const pnlHtml = signal.live_data?.live_pnl !== undefined 
                ? formatPct(signal.live_data.live_pnl, true) 
                : `<span class="text-text-muted">--.--%</span>`;
            const pnlSpan = signal.lock_status !== 'unlocked'
                ? `<span class="text-right font-bold text-lg text-text-muted">${formatPct(signal.live_data.live_pnl, true).replace(/<[^>]*>/g, '')}</span>`
                : `<span data-live="pnl" class="text-right font-bold text-lg">${pnlHtml}</span>`;

            const livePnl = signal.live_data?.live_pnl;
            const maxRoi = signal.max_roi;
            let displayAth = maxRoi; 
            if (livePnl !== undefined && (maxRoi === null || livePnl > maxRoi)) {
                displayAth = livePnl;
            }
            const athHtml = displayAth !== null ? formatPct(displayAth, true) : `<span class="text-text-muted">--.--%</span>`;
            const marketData = signal.live_data || {};

            const priceChange1h = marketData.priceChange?.h1;
            const priceChange24h = marketData.priceChange?.h24;
            const h1Html = priceChange1h !== undefined ? formatPct(priceChange1h) : `<span class="text-text-muted">--.--%</span>`;
            const h24Html = priceChange24h !== undefined ? formatPct(priceChange24h) : `<span class="text-text-muted">--.--%</span>`;

            return `
                <div x-show="activeVariantIndex === ${index}" x-cloak
                    class="grid grid-cols-2 gap-x-4 gap-y-2 text-xs font-mono border-t border-secondary pt-3"
                    data-call-id="${signal.call_id}" data-entry-price="${signal.entry_price_usd || ''}" data-last-pnl="${signal.live_data?.live_pnl || 0}">
                    
                    <span class="text-text-base">LIVE_PNL</span>${pnlSpan}
                    <span class="text-text-base">SGNL_ATH</span><span class="text-right font-bold">${athHtml}</span>
                    <span class="text-text-base">ENTRY_MCAP</span><span class="text-right font-bold text-text-base">${formatNumber(signal.entry_market_cap_usd, true) || '--'}</span>
                    <span class="text-text-base">MKT_CAP</span><span class="text-right font-bold text-text-base">${formatNumber(marketData.marketCap, true) || '--'}</span>
                    <span class="text-text-base">VOL_24H</span><span class="text-right font-bold text-text-base">${formatNumber(marketData.volume?.h24, true) || '--'}</span>
                    <span class="text-text-base">CHG_1H</span><span class="text-right font-bold">${h1Html}</span>
                    <span class="text-text-base">CHG_24H</span><span class="text-right font-bold">${h24Html}</span>
                </div>
            `;
        }).join('');

        return `<div class="relative">${panels}</div>`;
    }
    
    _buildCardSourceGrid(variants) {
        const sourceNameAndSwitcherHtml = `
            <div class="flex items-center justify-between border-t border-secondary pt-3">
                <div class="flex items-center space-x-2 min-w-0">
                    ${variants.map((signal, index) => `
                        <div x-show="activeVariantIndex === ${index}" class="flex items-center space-x-2 min-w-0" x-cloak>
                            <span class="text-lg">${this._getTierClasses(signal.source_tier).icon}</span>
                            <span class="text-sm font-semibold text-text-base truncate">${signal.source_name}</span>
                        </div>
                    `).join('')}
                </div>
                
                <div x-show="totalVariants > 1" class="flex items-center space-x-1 flex-shrink-0" x-cloak>
                    <button @click="activeVariantIndex = (activeVariantIndex - 1 + totalVariants) % totalVariants" class="p-1 rounded-full text-text-muted hover:bg-secondary hover:text-primary">&lt;</button>
                    <span class="font-mono text-xs text-text-dark w-6 text-center">
                        <span x-text="activeVariantIndex + 1"></span>/<span x-text="totalVariants"></span>
                    </span>
                    <button @click="activeVariantIndex = (activeVariantIndex + 1) % totalVariants" class="p-1 rounded-full text-text-muted hover:bg-secondary hover:text-primary">&gt;</button>
                </div>
            </div>
        `;

        const statsPanelsHtml = variants.map((signal, index) => {
            const profile = signal.source_profile || {};
            const isLocked = signal.lock_status !== 'unlocked';

            // --- Reusable Stat Formatting ---
            const medianRoi = profile.typical_outcome_roi !== undefined ? formatPct(profile.typical_outcome_roi, true) : 'N/A';
            const hitRate = profile.hit_rate_pct !== undefined ? formatPct(profile.hit_rate_pct) : 'N/A';
            
            const profitFactor = profile.profit_factor !== null && profile.profit_factor !== undefined ? `${Number(profile.profit_factor).toFixed(2)}x` : 'N/A';
            const profitFactorColor = (profile.profit_factor ?? 0) >= 1.5 ? 'text-success' : ((profile.profit_factor ?? 0) >= 1.0 ? 'text-yellow-400' : 'text-danger');

            const winRate5x = profile.win_rate_5x_pct !== undefined ? formatPct(profile.win_rate_5x_pct) : 'N/A';
            
            const rugPullPct = profile.rug_pull_percentage;
            const rugPullFormatted = rugPullPct !== null && rugPullPct !== undefined ? `${Number(rugPullPct).toFixed(1)}%` : 'N/A';
            const rugPullColor = (rugPullPct ?? 0) > 10 ? 'text-danger' : 'text-success';
            
            const avgRoi = profile.perfect_execution_roi !== undefined ? formatPct(profile.perfect_execution_roi, true) : 'N/A';

            if (isLocked) {
                // --- Render the horizontal scroller for LOCKED signals ---
                return `
                    <div x-show="activeVariantIndex === ${index}" x-cloak class="hide-scrollbar flex space-x-2 mt-2 -mx-4 px-4 overflow-x-auto text-xs font-mono [mask-image:linear-gradient(to_right,transparent,white_1rem,white_calc(100%-1rem),transparent)]">
                        <div class="text-center flex-shrink-0 w-24 border-r border-secondary/50 pr-2"><p class="text-text-muted">MEDIAN ROI</p><p class="font-bold">${medianRoi}</p></div>
                        <div class="text-center flex-shrink-0 w-24 border-r border-secondary/50 pr-2"><p class="text-text-muted">AVG ROI</p><p class="font-bold">${avgRoi}</p></div>
                        <div class="text-center flex-shrink-0 w-24 border-r border-secondary/50 pr-2"><p class="text-text-muted">HIT RATE</p><p class="font-bold">${hitRate}</p></div>
                        <div class="text-center flex-shrink-0 w-24 border-r border-secondary/50 pr-2"><p class="text-text-muted">P FACTOR</p><p class="font-bold ${profitFactorColor}">${profitFactor}</p></div>
                        <div class="text-center flex-shrink-0 w-24 border-r border-secondary/50 pr-2"><p class="text-text-muted">5X RATE</p><p class="font-bold">${winRate5x}</p></div>
                        <div class="text-center flex-shrink-0 w-24"><p class="text-text-muted">RUG %</p><p class="font-bold ${rugPullColor}">${rugPullFormatted}</p></div>
                    </div>
                `;
            } else {
                // --- Render the static 2x3 grid for UNLOCKED signals ---
                return `
                    <div x-show="activeVariantIndex === ${index}" x-cloak class="grid grid-cols-3 gap-x-4 gap-y-2 text-xs font-mono mt-2">
                        <div class="text-center"><p class="text-text-muted">MEDIAN ROI</p><p class="font-bold">${medianRoi}</p></div>
                        <div class="text-center"><p class="text-text-muted">HIT RATE</p><p class="font-bold">${hitRate}</p></div>
                        <div class="text-center"><p class="text-text-muted">P FACTOR</p><p class="font-bold ${profitFactorColor}">${profitFactor}</p></div>
                        <div class="text-center"><p class="text-text-muted">AVG ROI</p><p class="font-bold">${avgRoi}</p></div>
                        <div class="text-center"><p class="text-text-muted">5X RATE</p><p class="font-bold">${winRate5x}</p></div>
                        <div class="text-center"><p class="text-text-muted">RUG %</p><p class="font-bold ${rugPullColor}">${rugPullFormatted}</p></div>
                    </div>
                `;
            }
        }).join('');

        return `
            <div class="space-y-0">
                ${sourceNameAndSwitcherHtml}
                <div class="relative h-12">
                    ${statsPanelsHtml}
                </div>
            </div>
        `;
    }
    _buildCardFooter(variants) {
        const panels = variants.map((signal, index) => {
            const isLocked = signal.lock_status !== 'unlocked';
            if (!isLocked) return `<div x-show="activeVariantIndex === ${index}" class="h-9" x-cloak></div>`;
            
            return `
                <div x-show="activeVariantIndex === ${index}" x-cloak class="pt-2">
                    <button @click="$dispatch('open-unlock-flow', ${JSON.stringify({call_id: signal.call_id, cost: signal.unlock_cost}).replace(/"/g, "'")})" 
                            class="w-full bg-primary/20 text-primary hover:bg-primary hover:text-background transition-colors font-semibold text-sm py-2 rounded-base font-mono">
                        > UNLOCK [${signal.unlock_cost} PTS]
                    </button>
                </div>
            `;
        }).join('');

        return `<div class="relative">${panels}</div>`;
    }
    buildTableHeader(sort) {
        const headers = [
            { label: 'SGNL', align: 'left' },
            { label: 'Live PnL', align: 'center', sortBy: 'live_data.live_pnl' },
            { label: 'SGNL ATH', align: 'center', sortBy: 'max_roi' },
            { label: 'Source', align: 'left', sortBy: 'source.tier_rank', defaultDir: 'asc' },
            { label: 'Age', align: 'right', sortBy: 'call_timestamp' }
        ];

        const headerHtml = headers.map(h => {
            if (h.sortBy) {
                const isSorting = sort.by === h.sortBy;
                const arrow = isSorting ? (sort.dir === 'asc' ? '▲' : '▼') : '';
                return `<th scope="col" class="px-4 py-2 text-${h.align} text-xs font-medium text-text-muted uppercase tracking-wider cursor-pointer hover:text-primary" data-sort-by="${h.sortBy}" data-default-dir="${h.defaultDir || 'desc'}">
                    <div class="flex items-center ${h.align === 'center' ? 'justify-center' : ''} ${h.align === 'right' ? 'justify-end' : ''} space-x-1"><span>${h.label}</span><span class="text-primary">${arrow}</span></div>
                </th>`;
            }
            return `<th scope="col" class="px-4 py-2 text-${h.align} text-xs font-medium text-text-muted uppercase tracking-wider">${h.label}</th>`;
        }).join('');

        return `<thead class="bg-secondary/50"><tr>${headerHtml}</tr></thead>`;
    }
    
    buildTableRow(signal) {
        if (signal.lock_status !== 'unlocked') {
            return `
                <tr data-call-id="${signal.call_id}" class="opacity-60">
                    <td class="px-4 py-3"><div class="text-sm font-bold text-text-muted">[LOCKED SGNL] by ${signal.source_name}</div></td>
                    <td colspan="3" class="px-4 py-3 text-center text-xs text-text-dark">Unlock for ${signal.unlock_cost} Points to view live data</td>
                    <td class="px-4 py-3 text-right"><button @click="$dispatch('open-unlock-flow', ${JSON.stringify({call_id: signal.call_id, cost: signal.unlock_cost}).replace(/"/g, "'")})" class="bg-primary/20 text-primary text-xs font-semibold rounded px-2 py-1">UNLOCK</button></td>
                </tr>`;
        }
        
        const tierIcon = this._getTierClasses(signal.source_tier).icon;
        const pnlHtml = signal.live_data?.live_pnl !== undefined ? formatPct(signal.live_data.live_pnl, true) : '...';
        const timeSince = new Date(signal.call_timestamp);
        const timeAgo = timeSince.toLocaleString([], { dateStyle: 'short', timeStyle: 'short'});

        const livePnl = signal.live_data?.live_pnl;
        const maxRoi = signal.max_roi;
        let displayAth = maxRoi;
        if (livePnl !== undefined && (maxRoi === null || livePnl > maxRoi)) {
            displayAth = livePnl;
        }
        const athHtml = displayAth !== null ? formatPct(displayAth, true) : '...';

        return `
            <tr data-call-id="${signal.call_id}" data-entry-price="${signal.entry_price_usd || ''}" data-last-pnl="${signal.live_data?.live_pnl || 0}" class="hover:bg-secondary/50">
                <td class="px-4 py-3">
                    <div class="flex items-center space-x-3">
                        <div class="w-8 h-8 rounded-full bg-secondary flex-shrink-0 flex items-center justify-center font-bold">${signal.asset_symbol.charAt(0)}</div>
                        <div>
                            <p class="text-sm font-bold text-text-base">$${signal.asset_symbol} <span class="text-xs font-normal px-1.5 py-0.5 rounded-full bg-secondary text-text-muted">${signal.asset.chain_id.toUpperCase()}</span></p>
                            <p class="text-xs text-text-muted">by <span class="font-semibold">${tierIcon} ${signal.source_name}</span></p>
                        </div>
                    </div>
                </td>
                <td data-live="pnl" class="px-4 py-3 text-center font-mono font-bold text-lg">${pnlHtml}</td>
                <td class="px-4 py-3 text-center font-mono text-sm font-bold text-success">${athHtml}</td>
                <td class="px-4 py-3 font-mono text-sm font-semibold text-text-base">${tierIcon} ${signal.source_tier.toUpperCase()}</td>
                <td class="px-4 py-3 text-right font-mono text-xs text-text-muted" title="${timeSince.toUTCString()}">${timeAgo}</td>
            </tr>`;
    }
// Add this new helper method
    _buildSourceStatsDisplay(signal, layout = 'grid') {
        const profile = signal.source_profile || {};
        
        // Format all the stats, providing fallbacks
        const typicalOutcome = (profile.typical_outcome_roi !== null && profile.typical_outcome_roi !== undefined)
            ? formatPct(profile.typical_outcome_roi, true)
            : '<span class="text-text-dark">N/A</span>';

        const profitFactor = (profile.profit_factor !== null && profile.profit_factor !== undefined) 
            ? `${Number(profile.profit_factor).toFixed(2)}x` 
            : 'N/A';
        const profitFactorColor = (profile.profit_factor ?? 0) >= 1.5 ? 'text-success' : ((profile.profit_factor ?? 0) >= 1.0 ? 'text-yellow-400' : 'text-danger');

        const hitRate = (profile.hit_rate_pct !== null && profile.hit_rate_pct !== undefined)
            ? formatPct(profile.hit_rate_pct)
            : '<span class="text-text-dark">N/A</span>';
        
        const rate2x = (profile.win_rate_2x_pct !== null && profile.win_rate_2x_pct !== undefined)
            ? formatPct(profile.win_rate_2x_pct)
            : '<span class="text-text-dark">N/A</span>';

        const rate5x = (profile.win_rate_5x_pct !== null && profile.win_rate_5x_pct !== undefined)
            ? formatPct(profile.win_rate_5x_pct)
            : '<span class="text-text-dark">N/A</span>';

        const rugPullPct = profile.rug_pull_percentage;
        const rugPullFormatted = (rugPullPct !== null && rugPullPct !== undefined) 
            ? `${Number(rugPullPct).toFixed(1)}%` 
            : 'N/A';
        const rugPullColor = (rugPullPct ?? 0) > 10 ? 'text-danger' : 'text-success';
        
        const stats = [
            { label: 'MEDIAN ROI', value: typicalOutcome },
            { label: 'P FACTOR', value: profitFactor, color: profitFactorColor },
            { label: 'HIT RATE', value: hitRate },
            { label: '2X RATE', value: rate2x },
            { label: '5X RATE', value: rate5x },
            { label: 'RUG %', value: rugPullFormatted, color: rugPullColor }
        ];

        if (layout === 'grid') {
            return `
                <div class="grid grid-cols-6 gap-x-4 text-center text-xs font-mono">
                    ${stats.map(stat => `
                        <div>
                            <p class="text-text-muted">${stat.label}</p>
                            <div class="font-bold ${stat.color || ''}">${stat.value}</div>
                        </div>
                    `).join('')}
                </div>
            `;
        } else { // layout === 'scroll'
            return `
                <div class="hide-scrollbar flex space-x-2 overflow-x-auto text-xs font-mono">
                    ${stats.map((stat, index) => `
                        <div class="text-center flex-shrink-0 w-24 ${index < stats.length - 1 ? 'border-r border-secondary/50 pr-2' : ''}">
                            <p class="text-text-muted">${stat.label}</p>
                            <div class="font-bold ${stat.color || ''}">${stat.value}</div>
                        </div>
                    `).join('')}
                </div>
            `;
        }
    }
    buildStreamTab(stream) {
        return `
            <a href="#" data-stream-slug="${stream.slug}" class="group transition-colors whitespace-nowrap py-1.5 flex items-center space-x-2">
                <span class="text-text-muted">[</span>
                <span class="text-text-muted group-hover:text-text-base font-semibold">${stream.name}</span>
                 <span class="stream-total-badge text-xs text-text-muted group-hover:text-text-base font-mono hidden ml-0.5"></span>
                <span class="stream-badge hidden font-bold text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded-full ml-1"></span>
                <span class="text-text-muted">]</span>
                <span class="opacity-0 group-hover:opacity-100 animate-cursor text-primary">_</span>
            </a>
        `;
    }

    _getTierClasses(tier) {
        return {
            'diamond': { glow: 'from-tier-diamond/50 via-green-900/40 to-background', icon: '💎' },
            'gold': { glow: 'from-tier-gold/60 via-background/20 to-background', icon: '🥇' },
            'silver': { glow: 'from-tier-silver/40 via-slate-800/50 to-background', icon: '🥈' },
            'bronze': { glow: 'from-tier-bronze/40 via-background/10 to-background', icon: '🥉' }
        }[tier] || { glow: 'from-secondary/30 via-background/10 to-background', icon: '👀' };
    }

    _renderSparkline(signal,sparklineData) {
        // THE FIX: Always use entry_price_usd, as it's now available for locked signals too.
        const entryPrice = parseFloat(signal.entry_price_usd);
        const prices = sparklineData || [];
        
        if (isNaN(entryPrice) || entryPrice === 0 || prices.length < 1) {
            return `<span class="text-xs text-text-dark">[Chart Data Pending]</span>`;
        }

        const allValues = [entryPrice, ...prices];
        const min = Math.min(...allValues);
        const max = Math.max(...allValues);
        const range = max - min || 1; 

        const width = 200;
        const height = 64;
        const toY = val => height - ((val - min) / range) * height;
        const entryY = toY(entryPrice);

        let pathData;
        if (prices.length < 2) {
            const singlePointY = prices.length > 0 ? toY(prices[0]) : entryY;
            pathData = `M 0 ${singlePointY} L ${width} ${singlePointY}`;
        } else {
            pathData = prices.map((p, i) => `${i === 0 ? 'M' : 'L'} ${(i / (prices.length - 1)) * width} ${toY(p)}`).join(' ');
        }
        
        const lastPrice = prices.length > 0 ? prices[prices.length - 1] : entryPrice;
        const color = lastPrice >= entryPrice ? '#22C55E' : '#E53E3E';

        return `
            <svg viewBox="0 0 ${width} ${height}" class="w-full h-full" preserveAspectRatio="none">
                <path d="${pathData}" fill="none" stroke="${color}" stroke-width="2"/>
                <line x1="0" y1="${entryY}" x2="${width}" y2="${entryY}" stroke="#707070" stroke-width="1" stroke-dasharray="2,2"/>
            </svg>
        `;
    }
}

export const v2TemplateBuilder = new V2TemplateBuilder();